import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  MoreHorizontal,
  Check,
  ArrowLeft,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useKycSubmissions } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { formatDistanceToNow } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { makeRequest } from "@/utils/makeRequest";
import { KycStatus, KycType, type KycSubmission } from "@/interfaces/kyc";
import { useNavigate } from "react-router-dom";
import { ErrorState } from "@/components/ErrorState";

type KycLevel = 1 | 2 | 3;
type DocumentGroups = Record<KycType, Record<KycLevel, string[]>>;
type KycField = (typeof fieldGroups)[KycType][number];

const rejectionOptions = [
  "Unclear photo",
  "Name mismatch",
  "Expired document",
  "Suspicious activity",
  "Document not valid",
  "Other",
];

const documentGroups: DocumentGroups = {
  PERSONAL: {
    1: ["GOVERNMENT_ID", "SELFIE_WITH_ID"],
    2: ["PROOF_OF_ADDRESS"],
    3: ["SOURCE_OF_FUNDS", "BANK_REFERENCE", "INCOME_PROOF"],
  },

  BUSINESS: {
    1: ["BUSINESS_REG_CERT", "DIRECTOR_ID", "DIRECTOR_SELFIE"],
    2: ["TAX_CLEARANCE", "BUSINESS_BANK_STATEMENT"],
    3: ["AUDITED_FINANCIALS", "TRADE_LICENSE"],
  },
};

const fieldGroups = {
  PERSONAL: [
    "nationality",
    "phoneNumber",
    "address",
    "city",
    "state",
    "postalCode",
    "country",
  ],
  BUSINESS: [
    "businessName",
    "registrationNumber",
    "businessType",
    "taxId",
    "website",
    "businessAddress",
    "city",
    "state",
    "country",
    "directorFullName",
    "directorId",
  ],
} as const;

export default function AdminCompliance() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const navigate = useNavigate();

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    useState<KycSubmission | null>(null);

  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");

  const [requestNote, setRequestNote] = useState("");
  const [requiredDocuments, setRequiredDocuments] = useState<string[]>([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);

  const queryClient = useQueryClient();

  // Fetch submissions with pagination
  const {
    data: kycResponse,
    isLoading,
    isError,
    refetch,
  } = useKycSubmissions({
    page,
    limit,
  });

  console.log("KYC", kycResponse);

  const kycSubmissions = kycResponse?.data || [];
  const meta = kycResponse?.meta || { totalPages: 1, page: 1 };
  const allKycs = kycSubmissions;

  const draftKycs = kycSubmissions.filter(
    (k: any) => k.status === KycStatus.DRAFT,
  );

  const pendingKycs = kycSubmissions.filter(
    (k: any) => k.status === KycStatus.PENDING,
  );

  const underReviewKycs = kycSubmissions.filter(
    (k: any) => k.status === KycStatus.UNDER_REVIEW,
  );

  const needsInfoKycs = kycSubmissions.filter(
    (k: any) => k.status === KycStatus.NEEDS_MORE_INFO,
  );

  const completedKycs = kycSubmissions.filter((k: any) =>
    [KycStatus.APPROVED, KycStatus.REJECTED].includes(k.status),
  );

  const canRequestInfo =
    selectedSubmission?.status === KycStatus.PENDING ||
    selectedSubmission?.status === KycStatus.UNDER_REVIEW;

  // Stats
  const stats = [
    {
      label: "Pending",
      value: pendingKycs.length,
      icon: Clock,
      color: "text-amber-500",
    },
    {
      label: "Under Review",
      value: underReviewKycs.length,
      icon: Eye,
      color: "text-blue-500",
    },
    {
      label: "Needs Info",
      value: needsInfoKycs.length,
      icon: AlertTriangle,
      color: "text-orange-500",
    },
    {
      label: "Completed",
      value: completedKycs.length,
      icon: CheckCircle2,
      color: "text-emerald-500",
    },
  ];

  const KycTable = ({
    data,
    onReview,
  }: {
    data: any[];
    onReview: (kyc: any) => void;
  }) => {
    return (
      <div className="space-y-3">
        {data.map((kyc: KycSubmission) => (
          <Card key={kyc.id} className="border shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-sm">
                    {kyc.formData?.formData?.fullName}
                  </p>

                  <p className="text-xs text-muted-foreground">
                    {kyc.user?.email}
                  </p>
                </div>

                <Badge className={statusBadgeClass[getStatusBadge(kyc.status)]}>
                  {kyc.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Type</p>

                  <p className="font-medium">{kyc.kycType}</p>
                </div>

                <div>
                  <p className="text-muted-foreground">Level</p>

                  <p className="font-medium">Level {kyc.level}</p>
                </div>

                <div>
                  <p className="text-muted-foreground">Submitted</p>

                  <p>
                    {formatDistanceToNow(new Date(kyc.createdAt), {
                      addSuffix: true,
                    })}
                  </p>
                </div>

                <div>
                  <p className="text-muted-foreground">ID</p>

                  <p>{kyc.id.slice(0, 8)}</p>
                </div>
              </div>

              <Button className="w-full" onClick={() => onReview(kyc)}>
                <Eye className="h-4 w-4 mr-2" />
                Review Application
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const getFields = (type: KycType): KycField[] => {
    return [...fieldGroups[type]]; // clone removes readonly issue
  };

  const relevantFields = selectedSubmission
    ? getFields(selectedSubmission.kycType as KycType)
    : [];

  const level = selectedSubmission?.level as KycLevel | undefined;
  const type = selectedSubmission?.kycType;

  const relevantDocumentGroups =
    selectedSubmission && type && level ? documentGroups[type][level] : [];

  const toggleDoc = (doc: string) => {
    setRequiredDocuments((prev) =>
      prev.includes(doc) ? prev.filter((d) => d !== doc) : [...prev, doc],
    );
  };

  const toggleField = (field: string) => {
    setRequiredFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
  };

  const resetRequestModal = () => {
    setRequestNote("");
    setRequiredDocuments([]);
    setRequiredFields([]);
  };

  // ===================== MUTATIONS =====================
  const approveMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) => {
      return await makeRequest.put(`/kyc-submission/admin/${id}/approve`, {
        note,
      });
    },
    onSuccess: () => {
      toast.success("KYC Approved successfully");
      queryClient.invalidateQueries({ queryKey: ["kyc-submissions:all"] });
      setShowReviewModal(false);
    },
    onError: () => toast.error("Failed to approve KYC"),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      return await makeRequest.put(`/kyc-submission/admin/${id}/reject`, {
        reason,
      });
    },
    onSuccess: () => {
      toast.success("KYC Rejected");
      queryClient.invalidateQueries({ queryKey: ["kyc-submissions:all"] });
      setShowRejectModal(false);
      setShowReviewModal(false);
    },
    onError: () => toast.error("Failed to reject KYC"),
  });

  const requestInfoMutation = useMutation({
    mutationFn: async ({
      id,
      note,
      requiredDocuments,
      requiredFields,
    }: {
      id: string;
      note: string;
      requiredDocuments?: string[];
      requiredFields?: string[];
    }) => {
      return await makeRequest.put(`/kyc-submission/admin/${id}/request-info`, {
        note,
        requiredDocuments,
        requiredFields,
      });
    },
    onSuccess: () => {
      toast.success("Request for more information sent");
      queryClient.invalidateQueries({ queryKey: ["kyc-submissions:all"] });
      setShowRequestModal(false);
      setShowReviewModal(false);
      resetRequestModal();
    },
    onError: () => toast.error("Failed to send request"),
  });

  const handleReview = (submission: KycSubmission) => {
    setSelectedSubmission(submission);

    setRequiredDocuments([]);
    setRequiredFields([]);
    setRequestNote("");

    // ENSURE REVIEW MODAL ALWAYS OPENS FIRST CLEAN STATE
    setShowRejectModal(false);
    setShowRequestModal(false);

    setShowReviewModal(true);

    navigate(`/admin/compliance/${submission.id}`);
  };

  const handleApprove = () => {
    if (!selectedSubmission) return;
    approveMutation.mutate({ id: selectedSubmission.id });
  };

  const handleReject = () => {
    if (!selectedSubmission || !rejectReason) return;

    const reason = rejectNotes
      ? `${rejectReason}: ${rejectNotes}`
      : rejectReason;

    rejectMutation.mutate({
      id: selectedSubmission.id,
      reason,
    });
  };

  const handleRequestMoreInfo = () => {
    if (!selectedSubmission) return;

    setShowReviewModal(false);

    setRequiredDocuments([]);
    setRequiredFields([]);
    setRequestNote("");

    setShowRequestModal(true);
  };

  console.log({
    id: selectedSubmission?.id,
    note: requestNote,
    requiredDocuments,
    requiredFields,
  });

  const handleSendRequest = () => {
    if (!selectedSubmission || !requestNote.trim()) {
      toast.error("Please provide a message");
      return;
    }

    requestInfoMutation.mutate({
      id: selectedSubmission.id,
      note: requestNote,
      requiredDocuments,
      requiredFields,
    });
  };

  const getStatusBadge = (status: KycStatus) => {
    switch (status) {
      case KycStatus.PENDING:
        return "amber";

      case KycStatus.UNDER_REVIEW:
        return "blue";

      case KycStatus.NEEDS_MORE_INFO:
        return "orange";

      case KycStatus.APPROVED:
        return "emerald";

      case KycStatus.REJECTED:
        return "red";

      default:
        return "gray";
    }
  };

  const statusBadgeClass = {
    amber: "bg-amber-500/15 text-amber-600 border border-amber-500/30",
    blue: "bg-blue-500/15 text-blue-600 border border-blue-500/30",
    orange: "bg-orange-500/15 text-orange-600 border border-orange-500/30",
    emerald: "bg-emerald-500/15 text-emerald-600 border border-emerald-500/30",
    red: "bg-red-500/15 text-red-600 border border-red-500/30",
    gray: "bg-gray-500/15 text-gray-600 border border-gray-500/30",
  } as const;

  if (isLoading) return <Loader type="bounce" />;

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
        }}
      />
    );

  return (
    <div className="space-y-6 px-3 sm:px-0">
      <Button variant="ghost" onClick={() => navigate(-1)}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
          KYC & Compliance
        </h1>
        <p className="text-muted-foreground">
          Manage user identity verification and compliance
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {stat.label}
                    </p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="w-full overflow-x-auto justify-start gap-1 scrollbar-hide">
          <TabsTrigger className="whitespace-nowrap" value="all">
            All
          </TabsTrigger>
          <TabsTrigger className="whitespace-nowrap" value="pending">
            Pending
          </TabsTrigger>
          <TabsTrigger className="whitespace-nowrap" value="review">
            Under Review
          </TabsTrigger>
          <TabsTrigger className="whitespace-nowrap" value="needs-info">
            Needs Info
          </TabsTrigger>
          <TabsTrigger className="whitespace-nowrap" value="completed">
            Completed
          </TabsTrigger>
        </TabsList>

        {/* All Tab */}
        <TabsContent value="all">
          <Card>
            <CardHeader>
              <CardTitle>All KYC Submissions</CardTitle>
              <CardDescription>
                Complete overview of all verification requests
              </CardDescription>
            </CardHeader>

            <CardContent>
              <KycTable data={allKycs} onReview={handleReview} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending KYC Submissions</CardTitle>
              <CardDescription>
                Review and approve user identity verification requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="md:hidden space-y-3">
                {pendingKycs.map((kyc: any) => (
                  <Card key={kyc.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">
                            {kyc.formData?.fullName}
                          </p>

                          <p className="text-xs text-muted-foreground">
                            {kyc.user?.email}
                          </p>
                        </div>

                        <Badge>Pending</Badge>
                      </div>

                      <Button
                        className="w-full mt-4"
                        onClick={() => handleReview(kyc)}
                      >
                        Review
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table>
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        ID
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        User
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Level
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Documents
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Submitted
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingKycs.length > 0 ? (
                      pendingKycs.map((kyc: any) => (
                        <tr key={kyc.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-4 text-sm font-medium">
                            {kyc.id.slice(0, 8)}...
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-sm font-medium">
                                {kyc.formData?.fullName ||
                                  `${kyc.user?.firstName || ""} ${kyc.user?.lastName || ""}`}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {kyc.user?.email}
                              </p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">Level {kyc.level}</Badge>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {kyc.documents?.length || 0} docs
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(kyc.createdAt), {
                              addSuffix: true,
                            })}
                          </td>
                          <td className="py-3 px-4">
                            <Badge
                              className={
                                statusBadgeClass[getStatusBadge(kyc.status)]
                              }
                            >
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={() => handleReview(kyc)}
                                variant="outline"
                                size="sm"
                                className="h-8"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleReview(kyc)}
                                    className="text-emerald-500"
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={handleRequestMoreInfo}
                                  >
                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                    Request More Info
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedSubmission(kyc);
                                      setShowRejectModal(true);
                                    }}
                                    className="text-red-500"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Reject
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-12 text-center text-muted-foreground"
                        >
                          No pending KYC submissions at the moment.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row gap-3 items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {meta.page} of {meta.totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPage((p) => Math.min(meta.totalPages, p + 1))
                    }
                    disabled={page === meta.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="review">
          <Card>
            <CardHeader>
              <CardTitle>Under Review</CardTitle>
              <CardDescription>
                Submissions being actively reviewed
              </CardDescription>
            </CardHeader>

            <CardContent>
              <KycTable data={underReviewKycs} onReview={handleReview} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="needs-info">
          <Card>
            <CardHeader>
              <CardTitle>Awaiting User Response</CardTitle>
              <CardDescription>
                Submissions where additional information was requested
              </CardDescription>
            </CardHeader>

            <CardContent>
              <KycTable data={needsInfoKycs} onReview={handleReview} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completed Tab */}
        <TabsContent value="completed">
          <Card>
            <CardHeader>
              <CardTitle>Completed KYC Submissions</CardTitle>
              <CardDescription>
                History of approved and rejected KYC applications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        ID
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        User
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Level
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Processed At
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedKycs.length > 0 ? (
                      completedKycs.map((kyc: any) => (
                        <tr key={kyc.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-4 text-sm font-medium">
                            {kyc.id.slice(0, 8)}...
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-sm font-medium">
                                {kyc.formData?.fullName ||
                                  `${kyc.user?.firstName || ""} ${kyc.user?.lastName || ""}`}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {kyc.user?.email}
                              </p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline">Level {kyc.level}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={getStatusBadge(kyc.status)}>
                              {kyc.status === KycStatus.APPROVED ? (
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )}
                              {kyc.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(kyc.updatedAt), {
                              addSuffix: true,
                            })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-12 text-center text-muted-foreground"
                        >
                          No completed KYC submissions yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row gap-3 items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {meta.page} of {meta.totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPage((p) => Math.min(meta.totalPages, p + 1))
                    }
                    disabled={page === meta.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab (unchanged) */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>KYC Settings</CardTitle>
              <CardDescription>
                Configure KYC requirements and verification levels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {[
                  {
                    level: 1,
                    title: "Level 1 - Basic Verification",
                    desc: "Email and phone verification",
                    status: "Required",
                  },
                  {
                    level: 2,
                    title: "Level 2 - Identity Verification",
                    desc: "Government ID and selfie",
                    status: "Required",
                  },
                  {
                    level: 3,
                    title: "Level 3 - Address Verification",
                    desc: "Proof of address document",
                    status: "Optional",
                  },
                ].map((item) => (
                  <div
                    key={item.level}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.desc}
                      </p>
                    </div>
                    <Badge
                      variant={
                        item.status === "Optional" ? "outline" : "default"
                      }
                    >
                      {item.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject KYC Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Rejection Reason</label>
              <Select onValueChange={setRejectReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {rejectionOptions.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Additional Notes</label>
              <Textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="Add more details..."
                className="min-h-[100px]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending
                  ? "Rejecting..."
                  : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request More Info Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[95vh] overflow-y-auto rounded-xl p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Request Additional Information</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Specify exactly what the user needs to provide to continue
              verification.
            </p>
          </DialogHeader>

          <div className="space-y-6">
            {/* ================= REQUIRED DOCUMENTS ================= */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Required Documents</p>
                <span className="text-xs text-muted-foreground">
                  Click to toggle selection
                </span>
              </div>

              {relevantDocumentGroups?.length ? (
                <div className="p-3 space-y-2 border rounded-md">
                  <p className="text-xs text-muted-foreground font-medium">
                    Level {selectedSubmission?.level}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {relevantDocumentGroups.map((doc) => {
                      const isActive = requiredDocuments.includes(doc);

                      return (
                        <button
                          key={doc}
                          type="button"
                          onClick={() => toggleDoc(doc)}
                          className={`text-xs px-2 py-1 rounded-full border transition flex items-center gap-1 ${isActive ? "bg-primary text-primary-foreground border-primary shadow-sm" : "bg-background text-muted-foreground border-border hover:bg-muted"}`}
                        >
                          {doc.replace(/_/g, " ")}
                          {isActive && <Check className="h-3 w-3" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No document requirements for this level.
                </p>
              )}
            </div>

            {/* ================= REQUIRED FIELDS ================= */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Required Fields</p>
                <span className="text-xs text-muted-foreground">
                  Click to toggle selection
                </span>
              </div>

              <div className="border rounded-lg bg-blue-50/30 dark:bg-blue-950/10 p-3">
                {relevantFields.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No field requirements configured for this level.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {relevantFields.map((field) => {
                      const isActive = requiredFields.includes(field);

                      return (
                        <button
                          key={field}
                          type="button"
                          onClick={() => toggleField(field)}
                          className={`text-xs px-3 py-1 rounded-full border transition flex items-center gap-1 ${isActive ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-background text-blue-600 border-blue-200 hover:bg-blue-50"} `}
                        >
                          <span>{field.replace(/([A-Z])/g, " $1")}</span>

                          {isActive && <Check className="h-3 w-3" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ================= MESSAGE ================= */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Message to User</p>

              <Textarea
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="Explain what is missing or unclear..."
                className="min-h-[120px] resize-none"
              />
              <p className="text-xs text-muted-foreground">
                This message will be visible to the user in their KYC dashboard.
              </p>
            </div>

            {/* ================= ACTIONS ================= */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => setShowRequestModal(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>

              <Button
                onClick={handleSendRequest}
                disabled={requestInfoMutation.isPending || !requestNote.trim()}
                className="w-full sm:w-auto"
              >
                {requestInfoMutation.isPending ? "Sending..." : "Send Request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
